import { Suspense, lazy, useEffect, useRef } from 'react';
import { useStore, DOC_KEY } from '@/state/store';
import { loadWorkspace, saveTool, scheduleSave, unionAssetIds } from '@/state/persistence';
import { useTheme } from '@/hooks/useTheme';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useUnlockLink } from '@/hooks/useUnlockLink';
import { useAddPhotos } from '@/hooks/useAddPhotos';
import { useIsCompact, useIsPhone } from '@/hooks/useMediaQuery';
import { filesFromDataTransfer } from '@/media/pick';
import { TopBar } from '@/components/layout/TopBar';
import { StatusBar } from '@/components/layout/StatusBar';
import { MobileBar } from '@/components/layout/MobileBar';
import { PhotoLibrary } from '@/components/photos/PhotoLibrary';
import { Inspector } from '@/components/inspector/Inspector';
import { Toasts } from '@/components/ui/Toasts';
import { Paywall } from '@/components/license/Paywall';
import { ToolChooser } from '@/components/tools/ToolChooser';
import { TOOLS } from '@/tools/registry';
import { cn } from '@/lib/utils';

// Both dialogs are opened by an explicit action, and the export one pulls in
// the canvas renderer. Keeping them out of the entry chunk is the difference
// between the studio being interactive on first paint and not.
const ExportDialog = lazy(() =>
  import('@/components/export/ExportDialog').then((m) => ({ default: m.ExportDialog })),
);
const ShortcutsDialog = lazy(() =>
  import('@/components/layout/ShortcutsDialog').then((m) => ({ default: m.ShortcutsDialog })),
);

export default function App() {
  useTheme();
  useKeyboardShortcuts();
  useUnlockLink();
  const tool = useStore((s) => s.tool);
  const leftOpen = useStore((s) => s.ui.leftOpen);
  const rightOpen = useStore((s) => s.ui.rightOpen);
  const exportOpen = useStore((s) => s.ui.exportOpen);
  const shortcutsOpen = useStore((s) => s.ui.shortcutsOpen);
  const setUi = useStore((s) => s.setUi);
  const addPhotos = useAddPhotos();
  const compact = useIsCompact();
  const phone = useIsPhone();

  // Restore the previous session — all three documents, whichever page this
  // is, so switching tools never lands on an empty canvas.
  useEffect(() => {
    void loadWorkspace().then(({ docs, tool: lastTool, hadSaved }) => {
      const store = useStore.getState();
      store.hydrate(docs);

      // Only the two dedicated pages insist on a tool: somebody arriving at
      // /carousel from a search result must land in the carousel. The home
      // page is the front door, so it defers to whatever was used last.
      const declared = document.body.dataset.tool;
      if (declared === 'splitter' || declared === 'carousel') return;

      if (lastTool && lastTool !== 'collage') {
        store.setTool(lastTool);
        // Keep the address bar telling the same story as the screen. A
        // crawler arrives without localStorage, so `/` still renders the
        // collage its canonical claims.
        history.replaceState(null, '', TOOLS[lastTool].path);
      }
      // Nothing saved anywhere means this browser has never used the app.
      if (!hadSaved) store.setUi({ chooserOpen: 'firstRun' });
    });
  }, []);

  // Autosave every document change, pruning blobs against all three documents.
  useEffect(
    () =>
      useStore.subscribe((state, prev) => {
        const keep = unionAssetIds([state.project, state.splitter, state.carousel]);
        for (const tool of ['collage', 'splitter', 'carousel'] as const) {
          const key = DOC_KEY[tool];
          if (state[key] !== prev[key]) scheduleSave(tool, state[key], keep);
        }
        if (state.tool !== prev.tool) saveTool(state.tool);
      }),
    [],
  );

  // Crossing the desktop breakpoint changes what the panels *are* — docked
  // columns or overlay drawers — so reset them to that mode's sane default.
  const wasCompact = useRef(compact);
  useEffect(() => {
    if (wasCompact.current === compact) return;
    wasCompact.current = compact;
    setUi({ leftOpen: !compact, rightOpen: !compact });
  }, [compact, setUi]);

  // Never let the browser navigate away when a photo is dropped outside a target.
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      const files = filesFromDataTransfer(e.dataTransfer);
      e.preventDefault();
      if (files.length) void addPhotos(files);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [addPhotos]);

  const Stage = TOOLS[tool].Stage;
  const closeDrawers = () => setUi({ leftOpen: false, rightOpen: false });
  // Drawers sit above the dimming backdrop, so they use the solid panel colour
  // instead of the translucent one the docked columns use.
  const drawerBase =
    'absolute inset-y-0 z-30 w-[min(86vw,320px)] bg-[var(--bg-panel-solid)] ' +
    'shadow-[var(--shadow-lg)] transition-transform duration-250 ' +
    '[transition-timing-function:var(--ease)] will-change-transform';

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-[var(--bg-app)]">
      {/* The studio is a canvas, not a document, so its one heading is for
          screen readers and search engines rather than for the layout. */}
      <h1 className="sr-only">{TOOLS[tool].title}</h1>
      <TopBar />

      <div className="relative flex min-h-0 flex-1">
        <aside
          aria-hidden={!leftOpen}
          className={
            compact
              ? cn(drawerBase, 'left-0 hairline-r', leftOpen ? 'translate-x-0' : '-translate-x-full')
              : cn(
                  'panel z-10 shrink-0 overflow-hidden hairline-r',
                  'transition-[width] duration-250 [transition-timing-function:var(--ease)]',
                  leftOpen ? 'w-[248px]' : 'w-0',
                )
          }
        >
          <div className={compact ? 'h-full w-full' : 'h-full w-[248px]'}>
            <PhotoLibrary onPicked={compact ? closeDrawers : undefined} />
          </div>
        </aside>

        <main className="relative flex min-w-0 flex-1 flex-col">
          <Suspense fallback={null}>
            <Stage onRequestPhotos={(cellId) => void addPhotos(undefined, cellId)} />
          </Suspense>
        </main>

        <aside
          aria-hidden={!rightOpen}
          className={
            compact
              ? cn(drawerBase, 'right-0 hairline-l', rightOpen ? 'translate-x-0' : 'translate-x-full')
              : cn(
                  'panel z-10 shrink-0 overflow-hidden hairline-l',
                  'transition-[width] duration-250 [transition-timing-function:var(--ease)]',
                  rightOpen ? 'w-[304px]' : 'w-0',
                )
          }
        >
          <div className={compact ? 'h-full w-full' : 'h-full w-[304px]'}>
            <Inspector onClose={compact ? closeDrawers : undefined} />
          </div>
        </aside>

        {compact && (leftOpen || rightOpen) && (
          <div
            className="animate-fade absolute inset-0 z-20 bg-black/30 backdrop-blur-[1px]"
            onPointerDown={closeDrawers}
            aria-hidden
          />
        )}
      </div>

      {phone ? <MobileBar /> : <StatusBar />}
      <ToolChooser />
      <Suspense fallback={null}>
        {exportOpen && <ExportDialog />}
        {shortcutsOpen && <ShortcutsDialog />}
      </Suspense>
      <Paywall />
      <Toasts />
    </div>
  );
}
